

/* eslint-disable prefer-destructuring */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { v2 as t } from "styles/tokens/v2";

// Outlined colours that read as a neutral secondary action in v2: a grey
// hairline and dark text, as the mockups' Cancel / Customise / Templates.
const V2_NEUTRAL = ["dark", "secondary", "light", "text", "grey", "white", "black"];

export default styled(Button)(({ theme, ownerState }) => {
  const { palette, functions, borders, boxShadows } = theme;
  const { color, variant, size, circular, iconOnly, darkMode } = ownerState;

  const { white, text, transparent, gradients, grey } = palette;
  const { boxShadow, linearGradient, pxToRem, rgba } = functions;
  const { borderRadius } = borders;
  const { colored } = boxShadows;

  // styles for the button with variant="contained"
  const containedStyles = () => {
    // background color value
    const backgroundValue = palette[color] ? palette[color].main : white.main;

    // backgroundColor value when button is focused
    const focusedBackgroundValue = palette[color] ? palette[color].focus : white.focus;

    // boxShadow value
    const boxShadowValue = colored[color]
      ? `${boxShadow([0, 3], [3, 0], palette[color].main, 0.15)}, ${boxShadow(
          [0, 3],
          [1, -2],
          palette[color].main,
          0.2
        )}, ${boxShadow([0, 1], [5, 0], palette[color].main, 0.15)}`
      : "none";

    // boxShadow value when button is hovered
    const hoveredBoxShadowValue = colored[color]
      ? `${boxShadow([0, 14], [26, -12], palette[color].main, 0.4)}, ${boxShadow(
          [0, 4],
          [23, 0],
          palette[color].main,
          0.15
        )}, ${boxShadow([0, 8], [10, -5], palette[color].main, 0.2)}`
      : "none";

    // color value
    let colorValue = white.main;

    if (!darkMode && (color === "white" || color === "light" || !palette[color])) {
      colorValue = text.main;
    } else if (darkMode && (color === "white" || color === "light" || !palette[color])) {
      colorValue = grey[600];
    }

    // color value when button is focused
    let focusedColorValue = white.main;

    if (color === "white") {
      focusedColorValue = text.main;
    } else if (color === "primary" || color === "error" || color === "dark") {
      focusedColorValue = white.main;
    }

    return {
      background: backgroundValue,
      color: colorValue,
      boxShadow: boxShadowValue,

      "&:hover": {
        backgroundColor: backgroundValue,
        boxShadow: hoveredBoxShadowValue,
      },

      "&:focus:not(:hover)": {
        backgroundColor: focusedBackgroundValue,
        boxShadow: palette[color]
          ? boxShadow([0, 0], [0, 3.2], palette[color].main, 0.5)
          : boxShadow([0, 0], [0, 3.2], white.main, 0.5),
      },

      "&:disabled": {
        backgroundColor: backgroundValue,
        color: focusedColorValue,
      },
    };
  };

  // styles for the button with variant="outlined"
  const outliedStyles = () => {
    // background color value
    const backgroundValue = color === "white" ? rgba(white.main, 0.1) : transparent.main;

    // color value
    const colorValue = palette[color] ? palette[color].main : white.main;

    // boxShadow value
    const boxShadowValue = palette[color]
      ? boxShadow([0, 0], [0, 3.2], palette[color].main, 0.5)
      : boxShadow([0, 0], [0, 3.2], white.main, 0.5);

    // border color value
    let borderColorValue = palette[color] ? palette[color].main : rgba(white.main, 0.75);

    if (color === "white") {
      borderColorValue = rgba(white.main, 0.75);
    }

    return {
      background: backgroundValue,
      color: colorValue,
      borderColor: borderColorValue,

      "&:hover": {
        background: transparent.main,
        borderColor: colorValue,
      },

      "&:focus:not(:hover)": {
        background: transparent.main,
        boxShadow: boxShadowValue,
      },

      "&:active:not(:hover)": {
        backgroundColor: colorValue,
        color: white.main,
        opacity: 0.85,
      },

      "&:disabled": {
        color: colorValue,
        borderColor: colorValue,
      },
    };
  };

  // styles for the button with variant="gradient"
  const gradientStyles = () => {
    // background value
    const backgroundValue =
      color === "warning"
        ? palette.brand?.goldGradient || linearGradient(gradients.warning.main, gradients.warning.state)
        : color === "white" || !gradients[color]
        ? white.main
        : linearGradient(gradients[color].main, gradients[color].state);

    // boxShadow value
    const boxShadowValue = colored[color]
      ? `${boxShadow([0, 3], [3, 0], palette[color].main, 0.15)}, ${boxShadow(
          [0, 3],
          [1, -2],
          palette[color].main,
          0.2
        )}, ${boxShadow([0, 1], [5, 0], palette[color].main, 0.15)}`
      : "none";

    // boxShadow value when button is hovered
    const hoveredBoxShadowValue = colored[color]
      ? `${boxShadow([0, 14], [26, -12], palette[color].main, 0.4)}, ${boxShadow(
          [0, 4],
          [23, 0],
          palette[color].main,
          0.15
        )}, ${boxShadow([0, 8], [10, -5], palette[color].main, 0.2)}`
      : "none";

    // color value — gold CTA uses navy text on gold (M3 Pearl spec)
    let colorValue = white.main;

    if (color === "warning") {
      colorValue = palette.dark?.main || "#0E2348";
    } else if (color === "white") {
      colorValue = text.main;
    } else if (color === "light") {
      colorValue = gradients.dark.state;
    }

    return {
      background: backgroundValue,
      color: colorValue,
      boxShadow: color === "warning" ? "none" : boxShadowValue,

      "&:hover": {
        boxShadow: color === "warning" ? "none" : hoveredBoxShadowValue,
        filter: color === "warning" ? "brightness(1.03)" : undefined,
      },

      "&:focus:not(:hover)": {
        boxShadow: color === "warning" ? "none" : boxShadowValue,
      },

      "&:disabled": {
        background: backgroundValue,
        color: colorValue,
      },
    };
  };

  // styles for the button with variant="text"
  const textStyles = () => {
    // color value
    const colorValue = palette[color] ? palette[color].main : white.main;

    // color value when button is focused
    const focusedColorValue = palette[color] ? palette[color].focus : white.focus;

    return {
      color: colorValue,

      "&:hover": {
        color: focusedColorValue,
      },

      "&:focus:not(:hover)": {
        color: focusedColorValue,
      },
    };
  };

  // styles for the button with circular={true}
  const circularStyles = () => ({
    borderRadius: borderRadius.section,
  });

  // styles for the button with iconOnly={true}
  const iconOnlyStyles = () => {
    // width, height, minWidth and minHeight values
    let sizeValue = pxToRem(40);

    if (size === "small") {
      sizeValue = pxToRem(32);
    } else if (size === "large") {
      sizeValue = pxToRem(48);
    }

    // padding value
    let paddingValue = `${pxToRem(11)} ${pxToRem(11)} ${pxToRem(10)}`;

    if (size === "small") {
      paddingValue = pxToRem(4.5);
    } else if (size === "large") {
      paddingValue = pxToRem(16);
    }

    return {
      width: sizeValue,
      minWidth: sizeValue,
      height: sizeValue,
      minHeight: sizeValue,
      padding: paddingValue,

      "& .material-icons": {
        marginTop: 0,
      },

      "&:hover, &:focus, &:active": {
        transform: "none",
      },
    };
  };

  // v2: flat buttons. The classic look's stacked coloured shadows, lift on
  // hover and 3px focus halo are replaced by a single keyboard focus ring.
  const v2Styles = () => {
    const flat = {
      boxShadow: "none",
      transform: "none",
      "&:hover, &:active, &:focus, &:focus:not(:hover), &:active:not(:hover)": {
        boxShadow: "none",
        transform: "none",
      },
      "&.Mui-focusVisible, &:focus-visible": {
        outline: `2px solid ${t.primary}`,
        outlineOffset: 2,
      },
    };
    if (variant === "outlined" && V2_NEUTRAL.includes(color)) {
      return {
        ...flat,
        background: t.surface,
        color: t.text,
        borderColor: t.border2,
        "&:hover": { background: t.hover, borderColor: t.border2, boxShadow: "none" },
        "&:active:not(:hover)": { background: t.hover, color: t.text, opacity: 1 },
        "&:disabled": { color: t.textDisabled, borderColor: t.border },
      };
    }
    if (variant === "outlined") {
      return {
        ...flat,
        background: t.surface,
        "&:active:not(:hover)": { background: t.surface, color: palette[color]?.main, opacity: 1 },
      };
    }
    if (variant !== "contained" && variant !== "gradient") return flat;
    // A filled grey button is the secondary action (Cancel): in v2 it reads
    // as the neutral outlined button, not a dark slab beside the primary.
    if (["secondary", "light", "grey"].includes(color)) {
      return {
        ...flat,
        background: t.surface,
        color: t.text,
        boxShadow: `inset 0 0 0 1px ${t.border2}`,
        "&:hover, &:focus:not(:hover)": { background: t.hover, color: t.text, boxShadow: `inset 0 0 0 1px ${t.border2}` },
        "&.Mui-disabled, &:disabled": { background: t.surface, color: t.textDisabled, boxShadow: `inset 0 0 0 1px ${t.border}`, opacity: 1 },
      };
    }
    // Filled buttons: a readable disabled state (classic fades the brand
    // fill until the label is lost).
    return {
      ...flat,
      "&.Mui-disabled, &:disabled": {
        background: t.hover,
        color: t.text3,
        opacity: 1,
        boxShadow: "none",
      },
    };
  };

  return {
    ...(variant === "contained" && containedStyles()),
    ...(variant === "outlined" && outliedStyles()),
    ...(variant === "gradient" && gradientStyles()),
    ...(variant === "text" && textStyles()),
    ...(circular && circularStyles()),
    ...(iconOnly && iconOnlyStyles()),
    ...(theme.pearlUi === "v2" && v2Styles()),
  };
});
